import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Plus, X } from 'lucide-react';
import WealthChatBot from '../../components/WealthChatBot';
import TranscriptionWidget from '../../components/TranscriptionWidget';
import AdvisorCompatibilityTab from './components/AdvisorCompatibility/AdvisorCompatibilityTab';
import ClientPersonaExplorerTab from './components/ClientPersonaExplorer/ClientPersonaExplorerTab';
import ClientRelationshipGraph from './components/ClientRelationship/ClientRelationshipGraph';
import TriggerNotificationsTab from './components/TriggerNotifications/TriggerNotificationsTab';
import NextBestConversationsTab from './components/NextBestConversations/NextBestConversationsTab';
import IntelligentAgendasTab from './components/IntelligentAgendas/IntelligentAgendasTab';
import ClientInsightsTab from './components/ClientInsights/ClientInsightsTab';
import LoadingState from './components/common/LoadingState';
import {
  fetchClients,
  fetchWidgetsAndTabs,
  fetchClientBehavioralData,
  createTab,
  deleteTab,
  updateTabName,
  createWidget,
  deleteWidget,
  fetchUseCases,
  fetchTools
} from './api';
import ClientProfile from './components/common/ClientProfile';
import TabNavigation from './components/common/TabNavigation';
import WidgetToolbar from './components/common/WidgetToolbar';
import WidgetGrid from './components/common/WidgetGrid';

const UHNWIObservatoryDashboard = () => {
  // State variables remain the same
  const [widgetContents, setWidgetContents] = useState({});
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userId, setUserId] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showTabPopup, setShowTabPopup] = useState(false);
  const [transcriptionText, setTranscriptionText] = useState("");
  const [useCases, setUseCases] = useState([]);
  const [tools, setTools] = useState([]);
  const [selectedUseCase, setSelectedUseCase] = useState("tst2");
  const [exportLoading, setExportLoading] = useState(false);
  const [tabs, setTabs] = useState([]);
  const [selectedTab, setSelectedTab] = useState("search-listing");
  const [newTabName, setNewTabName] = useState("");
  const [hoveredTab, setHoveredTab] = useState(null);
  const [editingTabId, setEditingTabId] = useState(null);
  const [editingTabName, setEditingTabName] = useState("");
  const [activeClientTab, setActiveClientTab] = useState(null);
  const [clientBehavioralData, setClientBehavioralData] = useState(null);

  const navigate = useNavigate();
  const initialFetchDone = useRef(false);
  const specialTabsCreated = useRef(false);
  const editTabInputRef = useRef(null);

  const [newWidget, setNewWidget] = useState({
    title: '',
    widgetType: 'text',
    tool: '',
    toolId: '',
    endpoint: '',
    prompt: '',
    visualPrompt: '',
    tabId: '',
    hasAvatar: false
  });


  const handleTranscriptionComplete = (text) => {
    setTranscriptionText(text);
  };

  useEffect(() => {
    const storedUserId = sessionStorage.getItem('user_id');
    setUserId(storedUserId || 'U1757535255');
  }, []);

  useEffect(() => {
    const loadUseCases = async () => {
      try {
        const data = await fetchUseCases();
        if (data.success && Array.isArray(data.data)) {
          setUseCases([{
            associated_DB: "A3GoldDemo",
            created_by: "Marta",
            description: "This is the dashboard which is introduces to the following\n1. Pre-Call Preparation\n2. Post-Call Analysis\n3. Compliance Review\n4. Quaterly Business Review",
            domain_id: "00003",
            id: "tst1",
            name: "Pre-Call Summary"
          },
          {
            associated_DB: "A3GoldDemo",
            created_by: null,
            description: "Live Transcription",
            domain_id: "00003",
            id: "00002",
            name: "On Call Intelligence"
          }]);
        } else {
          throw new Error("Invalid data format received from API");
        }
      } catch (err) {
        console.error("Error fetching use cases:", err);
      }
    };

    loadUseCases();
  }, []);

  useEffect(() => {
    const loadTools = async () => {
      try {
        const data = await fetchTools();
        if (data.success && Array.isArray(data.data)) {
          setTools(data.data);

          if (data.data.length > 0) {
            const defaultTool = data.data[0];
            setNewWidget(prev => ({
              ...prev,
              tool: defaultTool.name,
              toolId: defaultTool.id,
              endpoint: defaultTool.api_endpoint || ''
            }));
          }
        } else {
          throw new Error("Invalid data format received from API");
        }
      } catch (err) {
        console.error("Error fetching tools:", err);
      }
    };

    loadTools();
  }, []);

  useEffect(() => {
    const loadClients = async () => {
      try {
        setLoading(true);
        const clients = await fetchClients();
        if (clients && Array.isArray(clients)) {
          setClients(clients);
          if (clients.length > 0) {
            setSelectedClient(clients[0]);
          }
        } else {
          throw new Error("Invalid data format received from API");
        }
      } catch (err) {
        console.error("Error fetching clients:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      loadClients();
    } else {
      setError("User ID is required to fetch clients");
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (selectedClient) {
      const loadClientBehavioralData = async () => {
        try {
          setLoading(true);
          const data = await fetchClientBehavioralData(selectedClient.id);
          if (data.success) {
            setClientBehavioralData({
              triggers: data.triggers || [],
              conversationTopics: data.conversationTopics || [],
              meetingAgendas: data.meetingAgendas || []
            });
          } else {
            throw new Error(data.error || "Failed to fetch client behavioral data");
          }
        } catch (err) {
          console.error("Error fetching client behavioral data:", err);
          // Set mock data as fallback
          setClientBehavioralData({
            triggers: [],
            conversationTopics: [],
            meetingAgendas: []
          });
        } finally {
          setLoading(false);
        }
      };

      loadClientBehavioralData();
    }
  }, [selectedClient]);

  const handleClientChange = (client) => {
    if (client.id === selectedClient?.id) return;
    setSelectedClient(client);
    setWidgetContents({});
    const updatedWidgets = widgets.map(widget => ({
      ...widget,
      key: `${widget.id}_${client.id}`,
      customerId: client.id
    }));
    setWidgets(updatedWidgets);
  };

  const handleUseCaseChange = (e) => {
    const useCaseId = e.target.value;
    setSelectedUseCase(useCaseId);
    setSelectedTab("client-overview");
    setActiveClientTab(null);
    specialTabsCreated.current = false;
    navigate(`/use-case/${useCaseId}`);
  };

  useEffect(() => {
    if (!userId || !selectedClient || !selectedUseCase) return;

    const loadWidgetsAndTabs = async () => {
      try {
        setLoading(true);
        const data = await fetchWidgetsAndTabs(selectedUseCase, userId);

        // Process tabs and widgets
        const fetchedTabs = data.tabs || [];
        setTabs(fetchedTabs);

        // If tabs exist, set the first one as active client tab
        if (fetchedTabs.length > 0 && !activeClientTab) {
          setActiveClientTab(fetchedTabs[0].tab_id);
        }

        // Process all widgets from all tabs
        const allWidgets = [];
        fetchedTabs.forEach(tab => {
          const tabWidgets = tab.widgets.map(widget => ({
            id: widget.widget_id,
            title: widget.widget_name,
            subtitle: widget.subtitle || "",
            prompt: widget.prompt || "",
            chartPrompt: widget.visual_prompt,
            customerId: selectedClient?.id,
            tool: widget.tool_name || "SQL Query Assistant",
            endpoint: widget.endpoint || ":5009/sql-ask",
            content: "",
            hasAvatar: widget.hasAvatar || false,
            key: `${widget.widget_id}_${selectedClient?.id}`,
            tabId: tab.tab_id
          }));
          allWidgets.push(...tabWidgets);
        });

        setWidgets(allWidgets);
        initialFetchDone.current = true;
      } catch (err) {
        console.error("Error fetching widgets and tabs:", err);
        setError(err.message);
        setWidgets([]);
        setTabs([]);
      } finally {
        setLoading(false);
      }
    };

    loadWidgetsAndTabs();
  }, [userId, selectedClient, selectedUseCase]);

  useEffect(() => {
    const createSpecialTabs = async () => {
      if (!loading && selectedUseCase && selectedClient) {
        // Check if special tabs exist
        const hasTriggerNotificationsTab = tabs.some(tab => tab.tab_name === "Trigger Notifications");
        const hasNextBestConversationsTab = tabs.some(tab => tab.tab_name === "Next Best Conversations");
        const hasIntelligentAgendasTab = tabs.some(tab => tab.tab_name === "Intelligent Agendas");

        // Create tabs if needed
        const newTabs = [];

        if (!hasTriggerNotificationsTab) {
          newTabs.push({
            tab_id: "static_trigger_notifications",
            tab_name: "Trigger Notifications",
            widgets: []
          });
        }

        if (!hasNextBestConversationsTab) {
          newTabs.push({
            tab_id: "static_next_best_conversations",
            tab_name: "Next Best Conversations",
            widgets: []
          });
        }

        if (!hasIntelligentAgendasTab) {
          newTabs.push({
            tab_id: "static_intelligent_agendas",
            tab_name: "Intelligent Agendas",
            widgets: []
          });
        }

        if (newTabs.length > 0) {
          setTabs(prevTabs => {
            // Filter out any existing special tabs to avoid duplicates
            const filteredTabs = prevTabs.filter(tab =>
              !tab.tab_id.startsWith("static_")
            );
            return [...filteredTabs, ...newTabs];
          });
        }

        specialTabsCreated.current = true;
      }
    };

    createSpecialTabs();
  }, [tabs.length, loading, selectedUseCase, selectedClient]);

  const hasTranscriptionWidget = widgets.some(widget =>
    widget.tool === "Transcript Insight Engine" || widget.tool === "Transcript Analysis"
  );

  const handleWidgetContentChange = (widgetId, content) => {
    setWidgetContents(prev => ({ ...prev, [widgetId]: content }));
  };

  const handleBackToHome = () => {
    navigate('/home');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setNewWidget(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'tool') {
      const selectedTool = tools.find(tool => tool.name === value);
      if (selectedTool) {
        setNewWidget(prev => ({
          ...prev,
          tool: selectedTool.name,
          toolId: selectedTool.id,
          endpoint: selectedTool.api_endpoint || ''
        }));
      }
    } else {
      setNewWidget(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleClientTabChange = (tabId) => {
    setActiveClientTab(tabId);
    setSelectedTab("client-overview");
  };

  const handleAddTab = async () => {
    if (!newTabName.trim()) {
      alert("Please enter a tab name");
      return;
    }

    try {
      const tabId = `tab_${Date.now().toString().slice(-6)}`;
      await createTab(tabId, newTabName, selectedUseCase);

      // Add the new tab to the local state
      const newTab = {
        tab_id: tabId,
        tab_name: newTabName,
        widgets: []
      };

      setTabs(prev => [...prev, newTab]);
      setActiveClientTab(tabId);
      setSelectedTab("client-overview");
      setNewTabName("");
      setShowTabPopup(false);
    } catch (err) {
      console.error("Error creating new tab:", err);
      alert("Failed to create new tab. Please try again.");
    }
  };

  const handleDeleteTab = async (tabId) => {
    if (!tabId) return;

    // Confirm before deleting
    if (!window.confirm("Are you sure you want to delete this tab? All widgets in this tab will also be deleted.")) {
      return;
    }

    try {
      await deleteTab(tabId);

      // Remove the tab and its widgets from local state
      setTabs(prev => prev.filter(tab => tab.tab_id !== tabId));
      setWidgets(prev => prev.filter(widget => widget.tabId !== tabId));

      // If the deleted tab was selected, select another tab
      if (activeClientTab === tabId) {
        const remainingTabs = tabs.filter(tab => tab.tab_id !== tabId);
        if (remainingTabs.length > 0) {
          setActiveClientTab(remainingTabs[0].tab_id);
        } else {
          setActiveClientTab(null);
        }
      }
    } catch (err) {
      console.error("Error deleting tab:", err);
      alert("Failed to delete tab. Please try again.");
    }
  };

  const handleSaveWidget = async () => {
    try {
      if (!activeClientTab) {
        alert("Please select a tab before adding a widget");
        return;
      }

      const widgetId = `${Date.now().toString().slice(-4)}`;
      const newWidgetObj = {
        id: widgetId,
        title: newWidget.title,
        subtitle: "",
        prompt: newWidget.prompt,
        chartPrompt: newWidget.widgetType === 'visualization' ? newWidget.visualPrompt : null,
        customerId: selectedClient?.id,
        tool: newWidget.tool,
        endpoint: newWidget.endpoint || ":5009/sql-ask",
        content: "",
        tabId: activeClientTab,
        hasAvatar: newWidget.hasAvatar
      };

      setWidgets(prev => [...prev, newWidgetObj]);

      await createWidget(newWidget, userId, selectedUseCase, activeClientTab);

      // Update the tabs state to include the new widget
      setTabs(prevTabs =>
        prevTabs.map(tab => {
          if (tab.tab_id === activeClientTab) {
            return {
              ...tab,
              widgets: [
                ...tab.widgets,
                {
                  widget_id: widgetId,
                  widget_name: newWidget.title,
                  subtitle: "",
                  prompt: newWidget.prompt,
                  visual_prompt: newWidget.widgetType === 'visualization' ? newWidget.visualPrompt : null,
                  tool_name: newWidget.tool,
                  endpoint: newWidget.endpoint || ":5009/sql-ask",
                  hasAvatar: newWidget.hasAvatar
                }
              ]
            };
          }
          return tab;
        })
      );

      setNewWidget({
        title: '',
        widgetType: 'text',
        tool: tools.length > 0 ? tools[0].name : '',
        toolId: tools.length > 0 ? tools[0].id : '',
        endpoint: tools.length > 0 ? (tools[0].api_endpoint || '') : '',
        prompt: '',
        visualPrompt: '',
        tabId: activeClientTab,
        hasAvatar: false
      });
      setShowPopup(false);
    } catch (err) {
      console.error("Error saving new widget:", err);
      alert("Failed to save widget. Please try again.");
    }
  };

  const handleTabDoubleClick = (tab) => {
    setEditingTabId(tab.tab_id);
    setEditingTabName(tab.tab_name);
  };

  useEffect(() => {
    if (editingTabId && editTabInputRef.current) {
      editTabInputRef.current.focus();
      editTabInputRef.current.select();
    }
  }, [editingTabId]);

  // Function to save the edited tab name
  const handleTabNameSave = async () => {
    if (!editingTabId || !editingTabName.trim()) {
      setEditingTabId(null);
      return;
    }

    try {
      await updateTabName(editingTabId, editingTabName);

      // Update the tabs state with the new name
      setTabs(prevTabs =>
        prevTabs.map(tab =>
          tab.tab_id === editingTabId
            ? { ...tab, tab_name: editingTabName }
            : tab
        )
      );
    } catch (err) {
      console.error("Error updating tab name:", err);
      alert("Failed to update tab name. Please try again.");
    } finally {
      setEditingTabId(null);
    }
  };

  // Handle key press events in the tab name input
  const handleTabNameKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleTabNameSave();
    } else if (e.key === 'Escape') {
      setEditingTabId(null);
    }
  };

  // Handle click outside to cancel editing
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (editingTabId && editTabInputRef.current && !editTabInputRef.current.contains(event.target)) {
        handleTabNameSave();
      }
    };

    if (editingTabId) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [editingTabId, editingTabName]);

  const navigateClientInsights = (client) => {
    if (client) {
      // Find matching client or use the provided one with complete data
      const matchingClient = clients.find(c => c.id === client.id) || client;
      setSelectedClient(matchingClient);
      setSelectedTab("client-overview");

      // Reset special tabs flag to force recreation
      specialTabsCreated.current = false;

      // Set the first tab as active if tabs exist
      if (tabs.length > 0) {
        setActiveClientTab(tabs[0].tab_id);
      }
    } else {
      setSelectedTab("client-overview");
    }
  };

  const renderTabContent = () => {
  if (selectedTab === "advisor-compatibility") {
    return <AdvisorCompatibilityTab />;
  } else if (selectedTab === "search-listing") {
    return <ClientPersonaExplorerTab navigateClientInsights={navigateClientInsights} />;
  } else if (selectedTab === "relationship-network") {
    return <ClientRelationshipGraph selectedClient={selectedClient} />;
  } else if (selectedTab === "client-overview") {
    // Always render ClientProfile and TabNavigation for all client insights tabs
    return (
      <>
        <ClientProfile
          widgetContents={widgetContents}
          clientData={selectedClient}
          allClients={clients}
          onClientChange={handleClientChange}
        />

        <TabNavigation
          tabs={tabs}
          activeClientTab={activeClientTab}
          handleClientTabChange={handleClientTabChange}
          editingTabId={editingTabId}
          editingTabName={editingTabName}
          editTabInputRef={editTabInputRef}
          handleTabDoubleClick={handleTabDoubleClick}
          handleTabNameKeyDown={handleTabNameKeyDown}
          handleTabNameSave={handleTabNameSave}
          hoveredTab={hoveredTab}
          setHoveredTab={setHoveredTab}
          handleDeleteTab={handleDeleteTab}
          setEditingTabName={setEditingTabName}
        />

        {/* Render the appropriate tab content based on activeClientTab */}
        {activeClientTab ? (
          activeClientTab === "static_trigger_notifications" ? (
            <div className="p-4">
              <TriggerNotificationsTab 
                clientBehavioralData={clientBehavioralData} 
                selectedClient={selectedClient} 
              />
            </div>
          ) : activeClientTab === "static_next_best_conversations" ? (
            <div className="p-4">
              <NextBestConversationsTab 
                clientBehavioralData={clientBehavioralData} 
                selectedClient={selectedClient} 
              />
            </div>
          ) : activeClientTab === "static_intelligent_agendas" ? (
            <div className="p-4">
              <IntelligentAgendasTab 
                clientBehavioralData={clientBehavioralData} 
                selectedClient={selectedClient} 
              />
            </div>
          ) : (
            <>
              <WidgetToolbar
                activeClientTab={activeClientTab}
                setShowPopup={setShowPopup}
              />
              <WidgetGrid
                widgets={widgets}
                activeClientTab={activeClientTab}
                selectedClient={selectedClient}
                widgetContents={widgetContents}
                handleWidgetContentChange={handleWidgetContentChange}
                deleteWidget={deleteWidget}
                selectedUseCase={selectedUseCase}
              />
            </>
          )
        ) : (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            {tabs.length === 0 ? (
              <>
                <div className="text-gray-400 mb-4">
                  <Plus size={48} className="mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">No tabs available</h3>
                <p className="text-sm text-gray-500 mb-4">Create a new tab to get started</p>
                <button
                  onClick={() => setShowTabPopup(true)}
                  className="px-4 py-2 bg-secondary text-white rounded-md font-medium text-sm transition-all hover:bg-secondaryDark"
                >
                  Create New Tab
                </button>
              </>
            ) : (
              <>
                <div className="text-gray-400 mb-4">
                  <Plus size={48} className="mx-auto" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">No tab selected</h3>
                <p className="text-sm text-gray-500 mb-4">Please select a tab to view its widgets</p>
              </>
            )}
          </div>
        )}
      </>
    );
  } else {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
        {tabs.length === 0 ? (
          <>
            <div className="text-gray-400 mb-4">
              <Plus size={48} className="mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No tabs available</h3>
            <p className="text-sm text-gray-500 mb-4">Create a new tab to get started</p>
            <button
              onClick={() => setShowTabPopup(true)}
              className="px-4 py-2 bg-secondary text-white rounded-md font-medium text-sm transition-all hover:bg-secondaryDark"
            >
              Create New Tab
            </button>
          </>
        ) : (
          <>
            <div className="text-gray-400 mb-4">
              <Plus size={48} className="mx-auto" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No tab selected</h3>
            <p className="text-sm text-gray-500 mb-4">Please select a tab to view its widgets</p>
          </>
        )}
      </div>
    );
  }
};

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-3">
      <div className="max-w-full mx-auto">
        <div className="flex justify-between items-center px-4 py-2 bg-white rounded-lg shadow-sm mx-4">
          <div className="flex items-center">
            <button
              onClick={handleBackToHome}
              className="flex items-center gap-1.5 text-secondary hover:text-secondaryDark transition-colors py-1.5 px-3 rounded-md hover:bg-gray-100"
            >
              <ChevronLeft size={18} />
            </button>
          </div>
          <div className="flex items-center">
            <img src="/capgemini-logo.png" alt="Capgemini Logo" className="h-6 w-auto object-contain" />
          </div>
        </div>
        <div className="mx-4 mt-4 border-b border-gray-200">
          <div className="flex items-center mb-4">
            {/* Fixed top-level tabs */}
            <button
              onClick={() => setSelectedTab("search-listing")}
              className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors ${selectedTab === "search-listing"
                ? 'border-secondary text-secondary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Search & listing
            </button>
            <button
              onClick={() => setSelectedTab("client-overview")}
              className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors ${selectedTab === "client-overview"
                ? 'border-secondary text-secondary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Client Insights
            </button>
            <button
              onClick={() => setSelectedTab("relationship-network")}
              className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors ${selectedTab === "relationship-network"
                ? 'border-secondary text-secondary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Relationship Network
            </button>
            {/* <button
              onClick={() => setSelectedTab("advisor-compatibility")}
              className={`py-2 px-4 text-sm font-medium border-b-2 transition-colors ${selectedTab === "advisor-compatibility"
                ? 'border-secondary text-secondary'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
            >
              Advisor Compatibility
            </button> */}


          </div>
        </div>
        

        {loading && !initialFetchDone.current ? (
          <LoadingState message="Loading client data..." />
        ) : (
          renderTabContent()
        )}

        {hasTranscriptionWidget && (
          <div className="mx-4 w-[calc(100%-2rem)] box-border rounded-lg">
            <TranscriptionWidget onTranscriptionComplete={handleTranscriptionComplete} />
          </div>
        )}

        <WealthChatBot cust_id={selectedClient?.id} />
      </div>

      {/* Add Widget Popup */}
      {showPopup && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-[1000] backdrop-blur-sm">
          <div className="bg-white rounded-lg p-6 w-[450px] max-w-[90%] shadow-xl border border-gray-200 animate-fadeInUp">
            <h2 className="mt-0 mb-5 text-xl font-semibold border-b border-gray-100 pb-3">Create New Widget</h2>

            <div className="mb-4">
              <label htmlFor="title" className="block mb-1.5 font-medium text-sm">Widget Title:</label>
              <input
                type="text"
                id="title"
                name="title"
                value={newWidget.title}
                onChange={handleInputChange}
                placeholder="Enter widget title"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm transition-all bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block mb-1.5 font-medium text-sm">Widget Type:</label>
              <div className="flex gap-5 py-2">
                <label className="flex items-center text-sm font-normal cursor-pointer">
                  <input
                    type="radio"
                    name="widgetType"
                    value="text"
                    checked={newWidget.widgetType === 'text'}
                    onChange={handleInputChange}
                    className="mr-1.5 accent-secondary cursor-pointer"
                  />
                  Text Widget
                </label>
                <label className="flex items-center text-sm font-normal cursor-pointer">
                  <input
                    type="radio"
                    name="widgetType"
                    value="visualization"
                    checked={newWidget.widgetType === 'visualization'}
                    onChange={handleInputChange}
                    className="mr-1.5 accent-secondary cursor-pointer"
                  />
                  Visualization Widget
                </label>
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="tool" className="block mb-1.5 font-medium text-sm">Tool:</label>
              <select
                id="tool"
                name="tool"
                value={newWidget.tool}
                onChange={handleInputChange}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm transition-all bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white"
              >
                {tools.map(tool => (
                  <option key={tool.id} value={tool.name}>
                    {tool.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label htmlFor="tabId" className="block mb-1.5 font-medium text-sm">Tab:</label>
              <select
                id="tabId"
                name="tabId"
                value={newWidget.tabId || activeClientTab}
                onChange={handleInputChange}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm transition-all bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white"
              >
                {tabs.map(tab => (
                  <option key={tab.tab_id} value={tab.tab_id}>
                    {tab.tab_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="mb-4">
              <label htmlFor="prompt" className="block mb-1.5 font-medium text-sm">Prompt:</label>
              <textarea
                id="prompt"
                name="prompt"
                value={newWidget.prompt}
                onChange={handleInputChange}
                placeholder="Enter the prompt for this widget"
                rows="3"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm transition-all bg-gray-50 resize-y min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white"
                required
              />
            </div>

            {newWidget.widgetType === 'visualization' && (
              <div className="mb-4">
                <label htmlFor="visualPrompt" className="block mb-1.5 font-medium text-sm">Visualization Prompt:</label>
                <textarea
                  id="visualPrompt"
                  name="visualPrompt"
                  value={newWidget.visualPrompt}
                  onChange={handleInputChange}
                  placeholder="Enter the visualization prompt"
                  rows="3"
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm transition-all bg-gray-50 resize-y min-h-[80px] focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white"
                  required
                />
              </div>
            )}

            {/* Avatar Option */}
            <div className="mb-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="hasAvatar"
                  checked={newWidget.hasAvatar}
                  onChange={handleInputChange}
                  className="w-4 h-4 accent-secondary cursor-pointer"
                />
                <span className="text-sm font-medium">Enable Avatar for this widget</span>
              </label>
              <p className="text-xs text-gray-500 mt-1 ml-6">
                When enabled, content will be presented with an interactive avatar
              </p>
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
              <button
                className="px-4 py-2 bg-gray-100 text-gray-600 rounded-md font-medium text-sm transition-all hover:bg-gray-200"
                onClick={() => setShowPopup(false)}
              >
                Cancel
              </button>
              <button
                className={`px-4 py-2 bg-secondary text-white rounded-md font-medium text-sm transition-all ${!newWidget.title || !newWidget.prompt || (newWidget.widgetType === 'visualization' && !newWidget.visualPrompt)
                  ? 'bg-secondary-light cursor-not-allowed'
                  : 'hover:bg-secondary-dark'
                  }`}
                onClick={handleSaveWidget}
                disabled={!newWidget.title || !newWidget.prompt || (newWidget.widgetType === 'visualization' && !newWidget.visualPrompt)}
              >
                Save Widget
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add Tab Popup */}
      {showTabPopup && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-[1000] backdrop-blur-sm">
          <div className="bg-white rounded-lg p-6 w-[400px] max-w-[90%] shadow-xl border border-gray-200 animate-fadeInUp">
            <h2 className="mt-0 mb-5 text-xl font-semibold border-b border-gray-100 pb-3">Create New Tab</h2>

            <div className="mb-4">
              <label htmlFor="tabName" className="block mb-1.5 font-medium text-sm">Tab Name:</label>
              <input
                type="text"
                id="tabName"
                value={newTabName}
                onChange={(e) => setNewTabName(e.target.value)}
                placeholder="Enter tab name"
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm transition-all bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:bg-white"
                required
              />
            </div>

            <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-100">
              <button
                className="px-4 py-2 bg-gray-100 text-gray-600 rounded-md font-medium text-sm transition-all hover:bg-gray-200"
                onClick={() => setShowTabPopup(false)}
              >
                Cancel
              </button>
              <button
                className={`px-4 py-2 bg-secondary text-white rounded-md font-medium text-sm transition-all ${!newTabName.trim()
                  ? 'bg-secondary-light cursor-not-allowed'
                  : 'hover:bg-secondary-dark'
                  }`}
                onClick={handleAddTab}
                disabled={!newTabName.trim()}
              >
                Create Tab
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UHNWIObservatoryDashboard;